// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {IContractDto, IContractStatus} from '../Contract.model'
import {PreviewCard} from '../../../common/component/PreviewCard'
import { SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import classNames from 'classnames'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { parseLocalDate } from '../../../util/Locale'
import { FREQUENCY } from '../../../util/Constants'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'

interface Props {
  contract?: IContractDto
  loading: boolean
  setRowSelected: (rowId?: string) => void
}

const ContractPreview: FC<Props> = ({contract, loading, setRowSelected}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/contracts/${contract?.id}`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/contracts/${contract?.id}/edit`),
    },
    {
      label: intl.formatMessage({id: 'UTIL.CREATE.ASSET'}),
      icon: 'bi-cpu',
      action: () => navigate(`/assets/new?contractId=${contract?.id}`),
    },
  ]

  return (
    <PreviewCard
      loading={loading}
      title={`${contract?.client?.displayName} - ${contract?.documentType}`}
      subTitle={contract?.sequence}
      pathOnClickHandler={`/contracts/${contract?.id}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        contract && (
          <div>
            <div className='row mt-10'>
              <div className='px-5'>
                <div className='row mb-5'>
                  <ClientEmbedered client={contract.client} />
                </div>
              </div>
            </div>

            <div className='row'>
              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={parseLocalDate(
                      contract.updatedAt,
                      intl.formatMessage({id: 'I18N.LOCALE'})
                    )}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${contract.sequence}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.CONTRACT'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/contracts/${contract.id}`}>
                          {`${contract.client?.displayName} - ${contract.documentType}`}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>

                  <div className='row mb-5'>
                    <label className='col-xxl-3 text-gray-700 fs-6'>
                      {intl.formatMessage({id: 'UTIL.STATUS'})}
                    </label>
                    <div
                      className={classNames({
                        'col-xxl-9 ps-6': contract.status === IContractStatus.ACTIVE,
                        'col-xxl-9 ps-6 text-danger fw-bold':
                          contract.status !== IContractStatus.ACTIVE,
                      })}
                    >
                      {intl.formatMessage({id: `UTIL.STATUS.${contract.status}`})}
                    </div>
                  </div>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DATE_START'})}
                    value={parseLocalDate(contract.startDate, '', '', {
                      hours: false,
                      minutes: false,
                      seconds: false,
                      millis: false,
                    })}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DATE_END'})}
                    value={parseLocalDate(contract.endDate, '', '', {
                      hours: false,
                      minutes: false,
                      seconds: false,
                      millis: false,
                    })}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DOCUMENT_TYPE'})}
                    value={`${contract.documentType}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.SERVICE_TYPE'})}
                    value={`${
                      contract.serviceFrequencyCode === FREQUENCY.OTHER
                        ? contract.serviceFrequency
                        : intl.formatMessage({
                            id: `DROPDOWN.FREQUENCY.${contract.serviceFrequencyCode}`,
                          })
                    }`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                    value={`${contract.serviceFrequency}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                    value={`${contract.serviceLocation || ''}`}
                  ></CustomInputRenderer>
                </div>
              </div>

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.METRICS'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    value={`${contract.totalAttachment || 0}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ASSETS'})}
                    value={`${contract.totalAsset || 0}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                    value={`${contract.totalReport || 0}`}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}

export {ContractPreview}
